import React, { useState, useEffect } from 'react';
import {
  Card, List, Button, Tag, Space, Typography, Row, Col,
  Input, Select, Tabs, Empty, Spin, Popconfirm, message,
  Rate, Tooltip
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined,
  SearchOutlined, FilterOutlined, UserOutlined, GlobalOutlined
} from '@ant-design/icons';
import PoolTableSVG from './PoolTableSVG';
import ExerciseEditor from './ExerciseEditor';
import {
  getExercises, getMyExercises, getPublicExercises,
  createExercise, updateExercise, deleteExercise
} from '../../api/services/exerciseService';
import { getGameTypes } from '../../api/services/gameTypeService';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const { Option } = Select;

/**
 * ExerciseList Component
 * Displays all exercises with filtering, viewing, and editing capabilities
 */
const ExerciseList = () => {
  const [exercises, setExercises] = useState([]);
  const [gameTypes, setGameTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorVisible, setEditorVisible] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [previewExercise, setPreviewExercise] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);

  // Filters
  const [searchText, setSearchText] = useState('');
  const [filterGameType, setFilterGameType] = useState(null);
  const [filterDifficulty, setFilterDifficulty] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  // Load exercises and game types
  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [exerciseData, gameTypeData] = await Promise.all([
        activeTab === 'my' ? getMyExercises() :
        activeTab === 'public' ? getPublicExercises() :
        getExercises(),
        getGameTypes()
      ]);
      setExercises(exerciseData);
      setGameTypes(gameTypeData);
    } catch (error) {
      console.error('Failed to load data:', error);
      message.error('Fehler beim Laden der Übungen.');
    } finally {
      setLoading(false);
    }
  };

  // Filter exercises
  const filteredExercises = exercises.filter(ex => {
    const matchesSearch = !searchText ||
      ex.name.toLowerCase().includes(searchText.toLowerCase()) ||
      (ex.tags && ex.tags.toLowerCase().includes(searchText.toLowerCase())) ||
      (ex.aufgabe && ex.aufgabe.toLowerCase().includes(searchText.toLowerCase()));

    const matchesGameType = !filterGameType || ex.game_type === filterGameType;
    const matchesDifficulty = !filterDifficulty || ex.difficulty === filterDifficulty;

    return matchesSearch && matchesGameType && matchesDifficulty;
  });

  // Handlers
  const handleCreate = () => {
    setEditingExercise(null);
    setEditorVisible(true);
  };

  const handleEdit = (exercise) => {
    setEditingExercise(exercise);
    setEditorVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteExercise(id);
      message.success('Übung gelöscht!');
      loadData();
    } catch (error) {
      message.error('Fehler beim Löschen.');
    }
  };

  const handleSave = async (exerciseData) => {
    setSaveLoading(true);
    try {
      if (editingExercise) {
        await updateExercise(editingExercise.id, exerciseData);
      } else {
        await createExercise(exerciseData);
      }
      setEditorVisible(false);
      loadData();
    } catch (error) {
      throw error;
    } finally {
      setSaveLoading(false);
    }
  };

  const getDifficultyLabel = (difficulty) => {
    const labels = {
      1: { text: 'Leicht', color: 'green' },
      2: { text: 'Mittel', color: 'blue' },
      3: { text: 'Schwer', color: 'orange' },
      4: { text: 'Experte', color: 'red' }
    };
    return labels[difficulty] || labels[2];
  };

  const getGameTypeName = (gameTypeId) => {
    const gt = gameTypes.find(g => g.id === gameTypeId);
    return gt ? gt.name : 'Unbekannt';
  };

  // Tab items
  const tabItems = [
    {
      key: 'all',
      label: (
        <span>
          <GlobalOutlined /> Alle Übungen
        </span>
      )
    },
    {
      key: 'my',
      label: (
        <span>
          <UserOutlined /> Meine Übungen
        </span>
      )
    },
    {
      key: 'public',
      label: (
        <span>
          <GlobalOutlined /> Öffentliche
        </span>
      )
    }
  ];

  return (
    <div style={{ padding: 16 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>Übungsbibliothek</Title>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Neue Übung
          </Button>
        </Col>
      </Row>

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        style={{ marginBottom: 16 }}
      />

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Suche nach Name, Tags..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Spieltyp"
              allowClear
              style={{ width: '100%' }}
              value={filterGameType}
              onChange={setFilterGameType}
            >
              {gameTypes.map(gt => (
                <Option key={gt.id} value={gt.id}>{gt.name}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Schwierigkeit"
              allowClear
              style={{ width: '100%' }}
              value={filterDifficulty}
              onChange={setFilterDifficulty}
            >
              <Option value={1}>Leicht</Option>
              <Option value={2}>Mittel</Option>
              <Option value={3}>Schwer</Option>
              <Option value={4}>Experte</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Exercise List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Spin size="large" />
        </div>
      ) : filteredExercises.length === 0 ? (
        <Empty
          description="Keine Übungen gefunden"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button type="primary" onClick={handleCreate}>
            Erste Übung erstellen
          </Button>
        </Empty>
      ) : (
        <List
          grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 3 }}
          dataSource={filteredExercises}
          renderItem={(exercise) => {
            const difficulty = getDifficultyLabel(exercise.difficulty);
            return (
              <List.Item>
                <Card
                  hoverable
                  actions={[
                    <Tooltip title="Ansehen" key="view">
                      <EyeOutlined onClick={() => setPreviewExercise(exercise)} />
                    </Tooltip>,
                    <Tooltip title="Bearbeiten" key="edit">
                      <EditOutlined onClick={() => handleEdit(exercise)} />
                    </Tooltip>,
                    <Popconfirm
                      key="delete"
                      title="Übung wirklich löschen?"
                      onConfirm={() => handleDelete(exercise.id)}
                      okText="Ja"
                      cancelText="Nein"
                    >
                      <DeleteOutlined style={{ color: '#ff4d4f' }} />
                    </Popconfirm>
                  ]}
                >
                  <Card.Meta
                    title={
                      <Space>
                        <Text strong>{exercise.name}</Text>
                        {exercise.is_public && (
                          <Tag color="blue" size="small">Öffentlich</Tag>
                        )}
                      </Space>
                    }
                    description={
                      <div>
                        <Space style={{ marginBottom: 8 }}>
                          <Tag color={difficulty.color}>{difficulty.text}</Tag>
                          <Tag>{getGameTypeName(exercise.game_type)}</Tag>
                        </Space>
                        <Paragraph
                          ellipsis={{ rows: 2 }}
                          style={{ marginBottom: 8, minHeight: 44 }}
                        >
                          {exercise.aufgabe}
                        </Paragraph>
                        {exercise.tags && (
                          <div>
                            {exercise.tags.split(',').slice(0, 3).map((tag, i) => (
                              <Tag key={i} size="small">{tag.trim()}</Tag>
                            ))}
                          </div>
                        )}
                      </div>
                    }
                  />

                  {/* Mini Preview */}
                  {exercise.baelle && exercise.baelle.length > 0 && (
                    <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>
                      <PoolTableSVG
                        balls={exercise.baelle}
                        scale={0.12}
                        interactive={false}
                        showGrid={false}
                        showRack={false}
                        useGridCoordinates={true}
                      />
                    </div>
                  )}
                </Card>
              </List.Item>
            );
          }}
        />
      )}

      {/* Exercise Editor Modal */}
      <ExerciseEditor
        visible={editorVisible}
        onClose={() => setEditorVisible(false)}
        onSave={handleSave}
        exercise={editingExercise}
        loading={saveLoading}
      />

      {/* Preview Modal */}
      {previewExercise && (
        <Card
          title={previewExercise.name}
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 600,
            zIndex: 1000,
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}
          extra={
            <Button onClick={() => setPreviewExercise(null)}>Schließen</Button>
          }
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Space>
                <Tag color={getDifficultyLabel(previewExercise.difficulty).color}>
                  {getDifficultyLabel(previewExercise.difficulty).text}
                </Tag>
                <Tag>{getGameTypeName(previewExercise.game_type)}</Tag>
                {previewExercise.is_public && <Tag color="blue">Öffentlich</Tag>}
              </Space>
            </Col>

            {previewExercise.baelle && previewExercise.baelle.length > 0 && (
              <Col span={24} style={{ display: 'flex', justifyContent: 'center' }}>
                <PoolTableSVG
                  balls={previewExercise.baelle}
                  scale={0.22}
                  interactive={false}
                  showGrid={false}
                  showRack={false}
                  useGridCoordinates={true}
                />
              </Col>
            )}

            <Col span={24}>
              <Title level={5}>Aufgabe</Title>
              <Paragraph>{previewExercise.aufgabe}</Paragraph>
            </Col>

            {previewExercise.beschreibung && (
              <Col span={24}>
                <Title level={5}>Beschreibung</Title>
                <Paragraph>{previewExercise.beschreibung}</Paragraph>
              </Col>
            )}

            {previewExercise.aufbau && (
              <Col span={24}>
                <Title level={5}>Aufbau</Title>
                <Paragraph>{previewExercise.aufbau}</Paragraph>
              </Col>
            )}

            {previewExercise.wertung && (
              <Col span={24}>
                <Title level={5}>Wertung</Title>
                <Paragraph>{previewExercise.wertung}</Paragraph>
              </Col>
            )}

            {previewExercise.tags && (
              <Col span={24}>
                <Title level={5}>Tags</Title>
                <Space wrap>
                  {previewExercise.tags.split(',').map((tag, i) => (
                    <Tag key={i}>{tag.trim()}</Tag>
                  ))}
                </Space>
              </Col>
            )}
          </Row>
        </Card>
      )}

      {/* Overlay for preview */}
      {previewExercise && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 999
          }}
          onClick={() => setPreviewExercise(null)}
        />
      )}
    </div>
  );
};

export default ExerciseList;
